import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  HStack,
  Input,
  Link,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Radio,
  RadioGroup,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { getSupabase, supabasePublic, supabaseSchema } from "./supabaseClient";

const DEFAULT_SETTINGS = {
  pre_offset_minutes: 30,
  post_mode: "offset",
  post_offset_minutes: 60,
  post_fixed_time: "23:59",
};

const AppointmentVersionSettings = ({ onClose }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [userTimezone, setUserTimezone] = useState(null); // null = not set

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      // Load schema-level version settings
      const { data, error } = await supabasePublic()
        .from("schema_settings")
        .select("appointment_version_settings")
        .eq("schema_name", supabaseSchema)
        .single();

      if (error && error.code !== "PGRST116") throw error;

      if (data?.appointment_version_settings) {
        setSettings({ ...DEFAULT_SETTINGS, ...data.appointment_version_settings });
      }

      // Load the current user's timezone
      const supabase = await getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: userRow } = await supabase
          .from("users")
          .select("timezone")
          .eq("id", session.user.id)
          .single();
        setUserTimezone(userRow?.timezone || null);
      }
    } catch (err) {
      console.error("Failed to load appointment version settings:", err);
      toast({
        title: "Failed to load settings",
        description: err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handlePostModeChange = (val) => {
    if (val === "fixed_time" && !userTimezone) {
      toast({
        title: "Timezone not set",
        duration: 6000,
        isClosable: true,
        render: ({ onClose: closeToast }) => (
          <Box
            bg="orange.500"
            color="white"
            px={4}
            py={3}
            borderRadius="md"
            boxShadow="md"
          >
            <Text fontWeight="bold" mb={1}>Timezone not set</Text>
            <Text fontSize="sm">
              Please set your timezone in{" "}
              <Link
                fontWeight="bold"
                textDecoration="underline"
                cursor="pointer"
                onClick={() => { closeToast(); navigate("/settings"); }}
              >
                Settings
              </Link>
              {" "}before using this option.
            </Text>
          </Box>
        ),
      });
      return; // don't update the mode
    }
    setSettings((s) => ({ ...s, post_mode: val }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { error } = await supabasePublic()
        .from("schema_settings")
        .upsert(
          { schema_name: supabaseSchema, appointment_version_settings: settings },
          { onConflict: "schema_name" }
        );

      if (error) throw error;

      toast({
        title: "Settings saved",
        description: "Appointment version timing updated.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      if (onClose) onClose();
    } catch (err) {
      console.error("Failed to save appointment version settings:", err);
      toast({
        title: "Failed to save settings",
        description: err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box p={4} textAlign="center">
        <Spinner />
        <Text mt={2} color="gray.500">Loading settings…</Text>
      </Box>
    );
  }

  const fixedTimeLabel = userTimezone
    ? `Fixed time on appointment day (${userTimezone})`
    : "Fixed time on appointment day (set your timezone in Settings first)";

  return (
    <VStack spacing={6} align="stretch" p={2}>
      <Box>
        <Text fontWeight="bold" fontSize="md" mb={1}>
          Pre-Clinic Version
        </Text>
        <Text fontSize="sm" color="gray.500" mb={3}>
          A snapshot of each document is created this many minutes before the
          scheduled appointment time.
        </Text>
        <FormControl maxW="260px">
          <FormLabel>Minutes before appointment</FormLabel>
          <NumberInput
            min={1}
            max={480}
            value={settings.pre_offset_minutes}
            onChange={(_, val) =>
              setSettings((s) => ({ ...s, pre_offset_minutes: val || 1 }))
            }
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <FormHelperText>Default: 30 minutes</FormHelperText>
        </FormControl>
      </Box>

      <Box borderTop="1px solid" borderColor="gray.200" pt={4}>
        <Text fontWeight="bold" fontSize="md" mb={1}>
          Post-Clinic Version
        </Text>
        <Text fontSize="sm" color="gray.500" mb={3}>
          Choose when to snapshot each document after the appointment.
        </Text>

        <FormControl mb={4}>
          <FormLabel>Timing mode</FormLabel>
          <RadioGroup value={settings.post_mode} onChange={handlePostModeChange}>
            <VStack align="flex-start" spacing={2}>
              <Radio value="offset">Minutes after appointment end</Radio>
              <Radio
                value="fixed_time"
                isDisabled={!userTimezone}
                color={!userTimezone ? "gray.400" : undefined}
              >
                <Text color={!userTimezone ? "gray.400" : undefined}>
                  {fixedTimeLabel}
                </Text>
              </Radio>
            </VStack>
          </RadioGroup>
        </FormControl>

        {settings.post_mode === "offset" ? (
          <FormControl maxW="260px">
            <FormLabel>Minutes after appointment</FormLabel>
            <NumberInput
              min={1}
              max={720}
              value={settings.post_offset_minutes}
              onChange={(_, val) =>
                setSettings((s) => ({ ...s, post_offset_minutes: val || 1 }))
              }
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <FormHelperText>Default: 60 minutes</FormHelperText>
          </FormControl>
        ) : (
          <FormControl maxW="200px">
            <FormLabel>Time of day</FormLabel>
            <Input
              type="time"
              value={settings.post_fixed_time}
              onChange={(e) =>
                setSettings((s) => ({ ...s, post_fixed_time: e.target.value }))
              }
            />
            <FormHelperText>
              Uses each user's timezone set in their profile. Default: 23:59
            </FormHelperText>
          </FormControl>
        )}
      </Box>

      <HStack pt={2}>
        <Button colorScheme="blue" onClick={handleSave} isLoading={isSaving} loadingText="Saving…">
          Save Settings
        </Button>
        <Button variant="ghost" onClick={loadSettings} isDisabled={isSaving}>
          Reset
        </Button>
      </HStack>
    </VStack>
  );
};

export default AppointmentVersionSettings;
